import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import FloatingButtons from '../components/FloatingButtons/FloatingButtons';
import ProductCard from '../components/Products/ProductCard';
import { newArrivals, featuredCollection, specialOne, highlights } from '../data/products';

const PartySupplies = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') || '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  // Get all products
  const allProducts = [
    ...newArrivals,
    ...featuredCollection,
    ...specialOne,
    ...highlights.birthday,
    ...highlights.anniversary,
    ...highlights.babyShower,
    ...highlights.gifting
  ];

  // Filter products based on type
  const getFilteredProducts = () => {
    if (!type) return [];
    
    const typeLower = type.toLowerCase();
    
    // Filter based on keywords related to party supplies
    return allProducts.filter(product => {
      const titleLower = product.title.toLowerCase();
      
      switch(typeLower) {
        case 'party-accessories':
          return titleLower.includes('accessory') || 
                 titleLower.includes('supply') ||
                 titleLower.includes('kit') ||
                 titleLower.includes('organiser') ||
                 titleLower.includes('box');
        case 'banners':
          return titleLower.includes('banner') || 
                 titleLower.includes('sign') ||
                 titleLower.includes('poster');
        case 'balloons':
          return titleLower.includes('balloon') && 
                 !titleLower.includes('foil');
        case 'foil-balloons':
          return titleLower.includes('foil') || 
                 titleLower.includes('metallic');
        default:
          return false;
      }
    });
  };

  const getTypeTitle = () => {
    const typeMap = {
      'party-accessories': 'Party Accessories',
      'banners': 'Banners',
      'balloons': 'Balloons',
      'foil-balloons': 'Foil Balloons'
    };
    
    return typeMap[type] || 'Party Supplies';
  };

  const getTypeDescription = () => {
    const descriptions = {
      'party-accessories': 'Complete your celebration with our wide range of party accessories including organizers, boxes, and decorative items.',
      'banners': 'Make a statement with our custom banners and signs for any occasion.',
      'balloons': 'Choose from our collection of colorful balloons to create stunning decorations.',
      'foil-balloons': 'Add elegance with our premium foil and metallic balloons for special moments.'
    };
    
    return descriptions[type] || 'Discover our amazing collection of party supplies.';
  };

  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#8B1538] to-[#a01d47] text-white py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              {getTypeTitle()}
            </h1>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
              {getTypeDescription()}
            </p>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-8 sm:py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: '🚚',
                  title: 'Fast Delivery',
                  description: 'Quick and reliable delivery across India'
                },
                {
                  icon: '✨',
                  title: 'Premium Quality',
                  description: 'High-quality products for lasting celebrations'
                },
                {
                  icon: '💳',
                  title: 'Easy Payment',
                  description: 'Multiple payment options available'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                  <div className="text-4xl mb-3">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredProducts.length > 0 ? (
              <>
                <div className="mb-8 flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                      Our {getTypeTitle()} Collection
                    </h2>
                    <p className="text-gray-600">
                      Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-8">
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} layout="grid" />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Explore Our {getTypeTitle()} Collection
                </h2>
                <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                  We're constantly adding new {getTypeTitle().toLowerCase()} to our collection. 
                  Browse our other products or contact us for custom orders!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/"
                    className="inline-block bg-[#8B1538] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#a01d47] transition-colors"
                  >
                    Browse All Products
                  </a>
                  <a
                    href="/contact"
                    className="inline-block border-2 border-[#8B1538] text-[#8B1538] px-8 py-3 rounded-lg font-semibold hover:bg-[#8B1538] hover:text-white transition-colors"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-[#8B1538] to-[#a01d47] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Need Custom {getTypeTitle()}?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              We offer custom designs and bulk orders. Get in touch with us to discuss your requirements!
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-[#8B1538] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Us Now
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default PartySupplies;

